import React, { useMemo, useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { calculateForecast, formatCurrency, formatDateShort, getPaymentTypeInfo } from '../utils/calculations';

const Dashboard: React.FC = () => {
  const { state, dispatch } = useAppContext();
  const [isEditingBalance, setIsEditingBalance] = useState(false);
  const [balanceInput, setBalanceInput] = useState(state.account.currentBalance.toString());


  const handleSaveBalance = () => {
    const newBalance = parseFloat(balanceInput);
    if (!isNaN(newBalance)) {
      dispatch({
        type: 'SET_ACCOUNT',
        payload: {
          currentBalance: newBalance,
          lastUpdated: new Date()
        }
      });
      setIsEditingBalance(false);
    }
  };

  const handleCancelBalance = () => {
    setBalanceInput(state.account.currentBalance.toString());
    setIsEditingBalance(false);
  };

  // Keep balance input in sync with account balance
  useEffect(() => {
    setBalanceInput(state.account.currentBalance.toString());
  }, [state.account.currentBalance]);

  const forecast = useMemo(() => {
    return calculateForecast(
      state.account,
      state.recurringPayments,
      state.oneOffPayments,
      state.paydayRules,
      state.pots,
      state.creditCards,
      31
    );
  }, [state]);

  const currentBalance = state.account.currentBalance;
  const balanceAfter31Days = forecast.length > 0 ? forecast[forecast.length - 1].balance : currentBalance;

  // Calculate baseline monthly flow from recurring payments only
  const { monthlyIncome, monthlyExpenses } = useMemo(() => {
    return state.recurringPayments
      .filter(payment => payment.isActive)
      .reduce(
        (acc, payment) => {
          if (payment.type === 'income') {
            acc.monthlyIncome += payment.amount;
          } else if (payment.type === 'expense') {
            acc.monthlyExpenses += payment.amount;
          }
          return acc;
        },
        { monthlyIncome: 0, monthlyExpenses: 0 }
      );
  }, [state.recurringPayments]);

  // Check if balance goes negative at any point in the next 31 days
  // Find the local minimum (lowest point before balance recovers to positive)
  const willGoNegative = forecast.some(entry => entry.balance < 0);
  const localMinimumEntry = useMemo(() => {
    if (!willGoNegative) return null;

    // Find the first negative entry
    const firstNegativeIndex = forecast.findIndex(entry => entry.balance < 0);
    if (firstNegativeIndex === -1) return null;

    // Track through the negative period to find the minimum
    let minEntry = forecast[firstNegativeIndex];

    for (let i = firstNegativeIndex + 1; i < forecast.length; i++) {
      const currentEntry = forecast[i];

      // If balance becomes positive, we've found the end of this negative period
      if (currentEntry.balance >= 0) {
        break;
      }

      // Track the minimum balance in this negative period
      if (currentEntry.balance < minEntry.balance) {
        minEntry = currentEntry;
      }
    }

    return minEntry;
  }, [forecast, willGoNegative]);

  const upcomingPayments = forecast.slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Account Overview</h2>

        {willGoNegative && localMinimumEntry && (
          <div className="mb-6 balance-card balance-card-negative" style={{padding: '12px'}}>
            <div style={{marginBottom: '8px', height: '28px', display: 'flex', alignItems: 'center'}}>
              <h3 className="text-lg font-semibold text-gray-700">⚠️ Balance Warning</h3>
            </div>
            <div style={{marginTop: '8px'}}>
              <p className="text-sm text-gray-700" style={{margin: '0', lineHeight: '1.4'}}>
                Your account balance will reach a minimum of{' '}
                <span className="font-medium text-red-600">{formatCurrency(localMinimumEntry.balance)}</span>
                {' '}on{' '}
                <span className="font-medium text-red-600">{formatDateShort(localMinimumEntry.date)}</span>
                {' '}after "{localMinimumEntry.description}" before recovering.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className={`balance-card ${currentBalance >= 0 ? 'balance-card-positive' : 'balance-card-negative'}`} style={{padding: '12px'}}>
            <div style={{marginBottom: '8px', height: '28px', display: 'flex', alignItems: 'center'}}>
              <h3 className="text-lg font-semibold text-gray-700">💰 Current Balance</h3>
            </div>
            {isEditingBalance ? (
              <div style={{marginTop: '8px'}} className="space-y-3">
                <input
                  type="number"
                  step="0.01"
                  value={balanceInput}
                  onChange={(e) => setBalanceInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter balance"
                  inputMode="decimal"
                  autoComplete="off"
                />
                <div className="flex space-x-2">
                  <button
                    onClick={handleSaveBalance}
                    className="btn-secondary text-xs px-3 py-1"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelBalance}
                    className="btn-danger text-xs px-3 py-1"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-end" style={{marginTop: '8px', marginBottom: '4px'}}>
                  <p className={`text-3xl font-bold ${currentBalance >= 0 ? 'balance-positive' : 'balance-negative'}`} style={{margin: '0'}}>
                    {formatCurrency(currentBalance)}
                  </p>
                  <button
                    onClick={() => setIsEditingBalance(true)}
                    className="btn-secondary text-xs"
                    style={{padding: '2px 8px', fontSize: '11px'}}
                  >
                    Edit
                  </button>
                </div>
                <p className="text-xs text-gray-500" style={{margin: '0'}}>
                  Last updated: {(() => {
                    const lastUpdated = state.account.lastUpdated;
                    if (lastUpdated instanceof Date && !isNaN(lastUpdated.getTime())) {
                      return `${lastUpdated.toLocaleDateString()} at ${lastUpdated.toLocaleTimeString()}`;
                    }
                    return 'Unknown';
                  })()}
                </p>
              </>
            )}
          </div>
          <div className={`balance-card ${balanceAfter31Days >= 0 ? 'balance-card-positive' : 'balance-card-negative'}`} style={{padding: '12px'}}>
            <div style={{marginBottom: '8px', height: '28px', display: 'flex', alignItems: 'center'}}>
              <h3 className="text-lg font-semibold text-gray-700">📈 Balance in 31 Days</h3>
            </div>
            <p className={`text-3xl font-bold ${balanceAfter31Days >= 0 ? 'balance-positive' : 'balance-negative'}`} style={{marginTop: '8px', margin: '0'}}>
              {formatCurrency(balanceAfter31Days)}
            </p>
          </div>
          <div className="balance-card" style={{padding: '12px'}}>
            <div style={{marginBottom: '8px', height: '28px', display: 'flex', alignItems: 'center'}}>
              <h3 className="text-lg font-semibold text-gray-700">📊 Baseline Monthly Flow</h3>
            </div>
            <div style={{marginTop: '8px'}}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Income:</span>
                <span className="text-lg font-semibold balance-positive">
                  {formatCurrency(monthlyIncome)}
                </span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Expenses:</span>
                <span className="text-lg font-semibold balance-negative">
                  {formatCurrency(monthlyExpenses)}
                </span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Net:</span>
                  <span className={`text-xl font-bold ${(monthlyIncome - monthlyExpenses) >= 0 ? 'balance-positive' : 'balance-negative'}`}>
                    {formatCurrency(monthlyIncome - monthlyExpenses)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Upcoming Payments</h2>
        {upcomingPayments.length === 0 ? (
          <p className="text-gray-500">No upcoming payments</p>
        ) : (
          <div className="table-container dashboard-upcoming">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="text-left">Description</th>
                  <th className="text-left">Date</th>
                  <th className="text-center">Source</th>
                  <th className="text-center">Type</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">New Balance</th>
                </tr>
              </thead>
              <tbody>
                {upcomingPayments.map((entry, index) => {
                  const paymentTypeInfo = getPaymentTypeInfo(entry);
                  return (
                    <tr key={index}>
                      <td className="text-left font-medium text-gray-800">{entry.description}</td>
                      <td className="text-left text-gray-600">{formatDateShort(entry.date)}</td>
                      <td className="text-center">
                        <span className={paymentTypeInfo.className}>
                          {paymentTypeInfo.label}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={`pill ${entry.amount >= 0 ? 'pill-success' : 'pill-error'}`}>
                          {entry.amount >= 0 ? '💰 Income' : '💸 Expense'}
                        </span>
                      </td>
                      <td className={`text-right font-semibold ${entry.amount >= 0 ? 'balance-positive' : 'balance-negative'}`}>
                        {formatCurrency(entry.amount)}
                      </td>
                      <td className={`text-right font-semibold ${entry.balance >= 0 ? 'balance-positive' : 'balance-negative'}`}>
                        {formatCurrency(entry.balance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">31-Day Forecast</h2>
        <div className="table-container dashboard-forecast" style={{maxHeight: '400px', overflowY: 'auto'}}>
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Description</th>
                <th className="text-center">Source</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {forecast.map((entry, index) => {
                const paymentTypeInfo = getPaymentTypeInfo(entry);
                return (
                  <tr key={index}>
                    <td className="text-left text-gray-600">{formatDateShort(entry.date)}</td>
                    <td className="text-left text-gray-800">{entry.description}</td>
                    <td className="text-center">
                      <span className={paymentTypeInfo.className}>
                        {paymentTypeInfo.label}
                      </span>
                    </td>
                    <td className={`text-right font-medium ${entry.amount >= 0 ? 'balance-positive' : 'balance-negative'}`}>
                      {formatCurrency(entry.amount)}
                    </td>
                    <td className={`text-right font-medium ${entry.balance >= 0 ? 'balance-positive' : 'balance-negative'}`}>
                      {formatCurrency(entry.balance)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;